import React from 'react';
import { Calendar, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const MonthlyDetailSection = () => {
  const scrollToBooking = () => {
    document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-black via-zinc-950 to-black relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-yellow-500/20 to-amber-400/20 border border-yellow-500/30 mb-6">
            <Sparkles className="w-5 h-5 text-yellow-400" />
            <span className="text-yellow-400 font-semibold">Best Value</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Monthly <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-400">Detail Plan</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Keep your vehicle looking showroom-fresh all month long with our recurring detail plan
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-zinc-900/50 backdrop-blur-sm border-2 border-yellow-500 rounded-2xl p-8 md:p-12 shadow-2xl shadow-yellow-500/10">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Week 1-2 */}
              <div className="bg-zinc-800/50 rounded-xl p-6 border border-yellow-500/20 hover:border-yellow-500 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-yellow-500/20 to-amber-400/20 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">2 Weeks</h3>
                    <p className="text-gray-400 text-sm">Every 2 weeks</p>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-400">
                    $50
                  </div>
                  <p className="text-gray-400">Exterior Detail</p>
                </div>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">Hand wash with premium soap</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">Wheel and tire cleaning</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">Tire shine application</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">Window cleaning (exterior)</span>
                  </li>
                </ul>
              </div>

              {/* Week 3-4 */}
              <div className="bg-zinc-800/50 rounded-xl p-6 border border-yellow-500/20 hover:border-yellow-500 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-yellow-500/20 to-amber-400/20 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">4 Weeks</h3>
                    <p className="text-gray-400 text-sm">Next 2 weeks</p>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-400">
                    $120
                  </div>
                  <p className="text-gray-400">Full Detail (Interior + Exterior)</p>
                </div>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">All exterior detailing services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">Complete interior vacuum</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">Dashboard & console cleaning</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">Window cleaning (all)</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Total Monthly Cost */}
            <div className="bg-gradient-to-r from-yellow-500/10 to-amber-400/10 border border-yellow-500/30 rounded-xl p-6 mb-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h4 className="text-white font-bold text-lg mb-2">Monthly Total</h4>
                  <p className="text-gray-400 text-sm">2 exterior details + 1 full detail every month</p>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-400">
                    $170<span className="text-2xl">/mo</span>
                  </div>
                  <p className="text-yellow-400 text-sm font-medium">Save vs. individual bookings</p>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="text-center">
                <div className="text-yellow-400 font-bold text-lg mb-1">Priority Scheduling</div>
                <p className="text-gray-400 text-sm">Reserved time slots</p>
              </div>
              <div className="text-center">
                <div className="text-yellow-400 font-bold text-lg mb-1">Consistent Care</div>
                <p className="text-gray-400 text-sm">Always looking fresh</p>
              </div>
              <div className="text-center">
                <div className="text-yellow-400 font-bold text-lg mb-1">Best Value</div>
                <p className="text-gray-400 text-sm">Lowest per-service cost</p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              {/* Requirement Notice */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6 max-w-2xl mx-auto">
                <p className="text-yellow-400 font-semibold text-sm">
                  ⓘ Required: One-time regular detail must be completed before starting the monthly plan
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  This ensures your vehicle starts the subscription in pristine condition
                </p>
              </div>

              <Button 
                onClick={scrollToBooking}
                className="bg-gradient-to-r from-yellow-500 to-amber-400 hover:from-yellow-600 hover:to-amber-500 text-black font-bold text-lg px-12 py-6 rounded-lg shadow-lg shadow-yellow-500/30 transition-all duration-300 hover:shadow-yellow-500/50 hover:scale-105"
              >
                Sign Up for Monthly Plan
              </Button>
              <p className="text-gray-500 text-sm mt-4">Cancel anytime • No contracts • Flexible scheduling</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MonthlyDetailSection;
